import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  detectLanguageFromPath,
  stripLanguagePrefix,
  resolveBlogSiblingPath,
} from '../utils/languageUtils';

export const useLanguage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = detectLanguageFromPath(location.pathname);

  useEffect(() => {
    if (i18n.language !== currentLang) i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  const changeLanguage = (newLang, options = {}) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) {
      console.warn(`Unsupported language: ${newLang}`);
      return;
    }

    const strippedPath = stripLanguagePrefix(location.pathname);
    const siblingPath = options.localeVariants
      ? resolveBlogSiblingPath(
          location.pathname,
          options.localeVariants,
          newLang,
          options.sourceLanguage,
          options.sourceSlug
        )
      : null;

    if (options.localeVariants && /^\/blog\/[^/]+$/.test(strippedPath) && !siblingPath) {
      return;
    }

    let newPath = siblingPath;
    if (!newPath) {
      newPath = newLang === DEFAULT_LANGUAGE
        ? strippedPath || '/'
        : `/${newLang}${strippedPath === '/' ? '' : strippedPath}`;
    }

    if (newPath !== '/' && newPath.endsWith('/')) newPath = newPath.slice(0, -1);
    navigate(newPath + location.search + location.hash);
  };

  const getLocalizedPath = (path) =>
    currentLang === DEFAULT_LANGUAGE ? path : `/${currentLang}${path}`;

  return {
    currentLang,
    changeLanguage,
    getLocalizedPath,
    isDefaultLanguage: currentLang === DEFAULT_LANGUAGE,
  };
};
